import { html, type TemplateResult } from 'lit';

import type { PreviewUploadView } from '../../types/views';
import { NEXT_BLOCKING_VIEWS } from '../../constants/views';
import {
  EMPTY_VALIDATION_SUMMARY,
  type ReviewValidationSummary,
} from '../csv-import-validation-summary/csv-import-validation-summary.js';

import './csv-import-review-body.js';

// ============================================================================
// Types
// ============================================================================

export interface ReviewStepProps {
  /** Replace instead of mutating in place so downstream Lit props update. */
  summary: ReviewValidationSummary;
  /** Screen-reader announcement for download status. Passed to review body's aria-live region. */
  announcement?: string | null;
}

// ============================================================================
// Module-level cached event dispatchers
// ============================================================================

const dispatchClose = (e: Event) => {
  const target = e.target as HTMLElement;
  target.dispatchEvent(
    new CustomEvent('csv-import-close', {
      bubbles: true,
      composed: true,
    }),
  );
};

const dispatchNext = (e: Event) => {
  const target = e.target as HTMLElement;
  target.dispatchEvent(
    new CustomEvent('csv-import-next', {
      bubbles: true,
      composed: true,
    }),
  );
};

// ============================================================================
// Step template function
// ============================================================================

/**
 * Returns the full step template for the Review step: body + footer buttons.
 * Intended to be rendered inside `<csv-import-modal-shell>`.
 *
 * Footer varies per view:
 * - `processing`: Next (disabled) + Cancel (disabled)
 * - `processing-delay`: Next (disabled) + Close (enabled)
 * - Default: Next (disabled if in NEXT_BLOCKING_VIEWS) + Cancel (enabled)
 */
export function reviewStepTemplate(
  view: PreviewUploadView,
  props: ReviewStepProps = { summary: EMPTY_VALIDATION_SUMMARY },
): TemplateResult {
  const nextDisabled = NEXT_BLOCKING_VIEWS.has(view);

  return html`
    <csv-import-review-body
      slot="step-body"
      .view=${view}
      .summary=${props.summary}
      .announcement=${props.announcement ?? null}
    ></csv-import-review-body>
    ${renderFooter(view, nextDisabled)}
  `;
}

// ============================================================================
// Footer rendering
// ============================================================================

function renderFooter(
  view: PreviewUploadView,
  nextDisabled: boolean,
): TemplateResult {
  if (view === 'processing') {
    return html`
      <inmar-hc-button
        slot="footer"
        label="Next"
        variant="primary"
        disabled
        @button-click=${dispatchNext}
      ></inmar-hc-button>
      <inmar-hc-button
        slot="footer"
        label="Cancel"
        variant="tertiary"
        disabled
        @button-click=${dispatchClose}
      ></inmar-hc-button>
    `;
  }

  if (view === 'processing-delay') {
    return html`
      <inmar-hc-button
        slot="footer"
        label="Next"
        variant="primary"
        disabled
        @button-click=${dispatchNext}
      ></inmar-hc-button>
      <inmar-hc-button
        slot="footer"
        label="Close"
        variant="tertiary"
        @button-click=${dispatchClose}
      ></inmar-hc-button>
    `;
  }

  // Default: all-valid, partial-valid, all-invalid, import-error, error-report-download-error
  return html`
    <inmar-hc-button
      slot="footer"
      label="Next"
      variant="primary"
      ?disabled=${nextDisabled}
      @button-click=${dispatchNext}
    ></inmar-hc-button>
    <inmar-hc-button
      slot="footer"
      label="Cancel"
      variant="tertiary"
      @button-click=${dispatchClose}
    ></inmar-hc-button>
  `;
}
